/**
 * Phase 11.3 — Agent-error toast helper.
 *
 * Public surface:
 *   showAgentError({ err, locale })
 *
 * Handles the 3 new LLM-agent error taxonomies introduced across
 * Phases 11.1/11.2:
 *   - 429 rate-limited  → shows `retry_after` seconds from the
 *                          `{detail: {error, retry_after}}` body.
 *   - 502 parse failure → parse-failure copy.
 *   - 504 timeout       → timeout copy (~30s upper bound).
 * Falls back to a generic error toast for other statuses.
 *
 * Locale = "de" | "en" (default "de" per tenant default).
 */
import { toast } from "sonner";

const COPY = {
  de: {
    rate: (r) =>
      `KI-Anfrage-Limit erreicht. Bitte in ${r}s wieder versuchen.`,
    parse:
      "KI-Antwort konnte nicht verarbeitet werden. Bitte erneut versuchen.",
    timeout:
      "KI-Anfrage hat länger als 30 Sekunden gedauert. Bitte erneut versuchen.",
    generic: "KI-Anfrage fehlgeschlagen. Bitte erneut versuchen.",
  },
  en: {
    rate: (r) =>
      `AI request limit reached. Please try again in ${r}s.`,
    parse: "AI response could not be parsed. Please retry.",
    timeout:
      "AI request timed out after 30 seconds. Please retry.",
    generic: "AI request failed. Please retry.",
  },
};

/** Extract HTTP status + parsed detail from an axios error. */
export function extractAgentErrorMeta(err) {
  const status = err?.response?.status || 0;
  const detail = err?.response?.data?.detail;
  const errorCode =
    (detail && typeof detail === "object" && detail.error) || null;
  const retryAfter =
    (detail && typeof detail === "object" && detail.retry_after) || null;
  return { status, errorCode, retryAfter };
}

/**
 * Show an agent-aware error toast.
 * Returns `{kind}` for jest assertions.
 */
export function showAgentError({ err, locale = "de" } = {}) {
  const lng = COPY[locale] ? locale : "de";
  const c = COPY[lng];
  const { status, errorCode, retryAfter } = extractAgentErrorMeta(err);

  let message;
  let kind = "generic";
  if (status === 429 || (errorCode || "").endsWith("_rate_limited")) {
    const seconds = Number.isFinite(retryAfter) ? retryAfter : "?";
    message = c.rate(seconds);
    kind = "rate_limited";
  } else if (status === 502) {
    message = c.parse;
    kind = "parse_failure";
  } else if (status === 504) {
    message = c.timeout;
    kind = "timeout";
  } else {
    message = c.generic;
  }
  toast.error(message, { duration: 6000, id: "agent-error-toast" });
  return { kind, message };
}
